/**
 * Spec №15.3 — the Staff-axis **desk** (`/app/staff/:agentId`).
 *
 * The agent's four-plane record (D-W1 / D-ST2), rendered from the SAME record
 * layer as the Matter Cockpit (`app-chrome.css` classes — `.matterhead`,
 * `.threeb`, `.recordgrid`, `.card`, `.panel`, `.timeline`, `.flowchip`, `.tab`),
 * built to the validated 15.3 design pack (`docs/mockups/15.3-staff-desk/`):
 *
 *   IS      — identity, persona, equipment (areas) + roles held.
 *   DOES    — task queue (get_agent_snapshot), declared routines, and the
 *             authorisations it has raised (awaiting a human).
 *   KNOW    — its memories (child memory nodes).
 *   HISTORY — its Activity.
 *
 * The headline is the **capability strip** — the agent's honest equivalent of the
 * Matter Cockpit's three-balances strip: Roles held · Acts in · The wall. It is
 * derived at runtime from live 11.2 area/role data (deriveCapabilities), never a
 * hard-coded verb list — X-4. Hire / fire / re-scope stay maker UX in the console
 * (C5); presence is derived, never simulated (D-A1/D-ST5); there is no run/execute
 * control (Kuvio executes nothing, D-A4).
 */
import { useState, type CSSProperties } from "react";
import { useParams, Link } from "react-router";
import { useLibraryNode, useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useAgentBrainSnapshot } from "../../hooks/use-agent-tasks-26";
import { useRoleAssignments, useRoles, useAreas } from "../../hooks/use-policy";
import { useAgentRoutines, useRunRoutine } from "../../hooks/use-routines";
import { usePendingAuthorisations } from "../../hooks/use-ledger-approvals";
import { useWorkforceStatus } from "../../hooks/use-workforce-status";
import { useWorkflowAdvance } from "../../hooks/use-workflow";
import { avatarColorFor, presenceLabel, deriveCapabilities } from "./staff-desk";
import { ExportKitSection } from "./ExportKitSection";
import { AutonomyDial } from "./AutonomyDial";
import { DecisionCard } from "../../components/collab/DecisionCard";
import { AuthRow } from "../../components/ledger/AuthRow";
import { useAnswerNeedsYou, useSkipNeedsYou, usePlayTaskRun } from "../../hooks/use-task-thread";
import { useJustAnswered, receiptFor, type AnsweredReceipt } from "../../hooks/use-just-answered";
import { useCoPanel } from "../../components/collab/co-panel-context";
import { formatRelativeTime } from "../../lib/v3-columns";
import { useAnchorStream } from "../../hooks/use-anchor-stream";
import type { AgentConfig, Task, TaskProvenance } from "../../../lib/types";
import { useStageHead } from "./stage-head";


/**
 * №22.4 (work item D) — how a run ENDED, for the desk digest. The important row is the
 * last one: a finished run with no recorded ending is not a success and must not look
 * like one. The absence IS the information.
 */
function runEnding(e: { termination?: string | null; degraded?: string | null; status?: string; terminationBasis?: string | null; missingKey?: string | null }): { glyph: string; colour: string; label: string } {
  if (e.degraded) {
    const why: Record<string, string> = {
      ai_off: "AI is off — nothing ran", no_runner: "no runner — nothing ran",
      no_credit: "AI credit exhausted — nothing ran", no_equipment: "no tool for this step",
      dial_off: "autonomy Off — parked as a draft", dial_supervised: "autonomy Supervised — waiting for ▶",
      run_failed: "the run failed — nothing ticked",
    };
    return { glyph: "!", colour: "var(--warn)", label: why[e.degraded] ?? "did not run" };
  }
  switch (e.termination) {
    case "done_with_receipts":
      return { glyph: "✓", colour: "var(--ok)", label: e.terminationBasis === "attestation" ? "done — attested" : "done — with receipts" };
    case "needs_you":
      return { glyph: "◑", colour: "var(--warn)", label: "needs you — raised a question and stopped" };
    case "blocked_honest":
      return { glyph: "⊘", colour: "var(--muted)", label: `blocked — lacks ${e.missingKey?.replace(/-/g, " ") ?? "a tool"}` };
    default:
      return e.status === "running"
        ? { glyph: "·", colour: "var(--muted)", label: "running…" }
        : { glyph: "·", colour: "var(--muted)", label: "ended without a recorded outcome — still standing" };
  }
}

/**
 * №22.4.1 (F1) — the clock on a live line. A live claim carries the time it
 * started ("since 09:41"), so "now:" can always be checked against something.
 * Local time, because the supervisor reading it is in the firm.
 */
function formatClockTime(iso: string): string {
  const ms = Date.parse(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(ms)) return "just now";
  return new Date(ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/**
 * №22.4.1 (F3) — the provenance line: what CREATED this task. The emphasis sits
 * on the source (the workflow, the routine, the person) because that is the part
 * a supervisor scans for. The parts arrive already decided by the engine
 * (`deriveTaskProvenance`) — this renders them and invents nothing.
 */
function ProvenanceLine({ p }: { p?: TaskProvenance | null }): React.ReactElement {
  if (!p) return <>source not recorded</>;
  return (
    <>
      {p.lead}
      {p.source ? <strong>{p.source}</strong> : "—"}
      {p.detail ? ` · ${p.detail}` : ""}
    </>
  );
}

export function StaffDeskPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const node = useLibraryNode(agentId);
  const wsInfo = useWorkspaceInfo();
  const snap = useAgentBrainSnapshot(agentId);
  const assignments = useRoleAssignments();
  const roles = useRoles();
  const areas = useAreas();
  const routines = useAgentRoutines(agentId);
  const auths = usePendingAuthorisations();
  const workforce = useWorkforceStatus(agentId ? [agentId] : [], !!agentId);
  const answer = useAnswerNeedsYou();
  // №22.5 (C4) — the desk answers cards, so the desk can skip them too.
  const skip = useSkipNeedsYou();
  const play = usePlayTaskRun(); // №22.4.1 (F4) — ▶ Run to complete is a real button
  const ja = useJustAnswered(); // №22.2 (D-CV1) — hook before any early return
  const coPanel = useCoPanel(); // №22.3 (D-CH4) — view thread → the docked panel
  // №22.4 (work item D) — WYWA renders from REAL RUN DATA. An activity says something
  // happened; a RUN says what the agent did, how it ENDED, and whether it ended at all.
  // Declared here with the other hooks, ABOVE the early returns — the 22.3.1 P3 lesson:
  // a hook after an early return drops on the other branch and crashes the surface.
  const deskStream = useAnchorStream(agentId ? { kind: "agent", id: agentId } : null, { limit: 40 });
  const runRoutine = useRunRoutine();
  // №22.6 — a workflow step with no task is completed by the ENGINE's own verb, the
  // same one the process page uses. The D-HM1 receipts wall applies to an AGENT
  // principal on a task-carrying step; a human ticking a task-less step is the
  // attestation path, so this needs no new gate and no server change.
  const advance = useWorkflowAdvance();

  if (node.isLoading) {
    return (
      <div className="screen">
        <div className="matterhead"><div className="skeleton sk-line" style={{ width: "34%", height: 22 }} /></div>
        <div className="threeb">{[0, 1, 2].map((i) => <div key={i} className="bal"><div className="skeleton sk-line" style={{ width: "50%" }} /><div className="skeleton sk-line" style={{ width: "70%", height: 20 }} /></div>)}</div>
      </div>
    );
  }
  const n = node.data;
  if (node.isError || !n || n.type !== "agent") {
    return (
      <div className="screen"><div className="error-state">
        <h3>Couldn’t load this desk</h3>
        <p>This Staff axis entry didn’t resolve to a member of staff. They may have been archived, or you may not have access.</p>
        <button data-variant="primary" onClick={() => node.refetch()}>Retry</button>
      </div></div>
    );
  }

  const cfg = (n.agent_config ?? null) as AgentConfig | null;
  const bio = (cfg as (AgentConfig & { bio?: string | null }) | null)?.bio ?? null;
  const template = (cfg as (AgentConfig & { hired_from_template?: { template_id: string; template_version: string } | null }) | null)?.hired_from_template ?? null;
  const persona = bio ?? n.description ?? "";
  const roster = (wsInfo.data?.library_root ?? []).filter((x) => x.type === "agent").map((x) => ({ id: x.id }));
  const ramp = avatarColorFor(agentId!, roster);
  const initial = n.name.slice(0, 1).toUpperCase();
  // №31 (D-CE1) — the hire's ADDRESS. Composed server-side (it spans the DO's local
  // part and the firm's claim in the global registry), so the desk either has a whole
  // address or none. Never render a partial one: a half address is one a client would
  // try to send to.
  const emailAddress = (n as { email_address?: string | null }).email_address ?? null;

  const st = workforce.data?.[agentId!];
  const presence = presenceLabel(st?.status);
  // №22.4.1 (F1) — the verb is a PRESENT-TENSE claim ("· Reviewing WIP"), so it
  // renders only alongside a timestamped live run. Without that test the identity
  // strip repeated the Working-Now defect in miniature: an agent that last did
  // something two days ago read as "· Ran routine: Morning inbox triage" with
  // nothing to date it. Same rule, both places.
  const presenceVerb = st?.since ? (st.verb ?? null) : null;

  const caps = deriveCapabilities(agentId!, assignments.data ?? [], roles.data ?? [], areas.data);
  const raisedAuths = (auths.data?.authorisations ?? []).filter((a) => a.requested_by.kind === "agent" && a.requested_by.id === agentId);

  const current = snap.data?.current ?? null;
  const upNext = snap.data?.up_next ?? [];
  const anchorLabelOf = (t: Task): string | undefined => {
    const a = t.anchor ?? null;
    return a ? (a.name && a.name !== a.no ? `${a.name} · ${a.no}` : a.no) : undefined;
  };
  const rtns = routines.data ?? [];
  const deskRuns = (deskStream.data?.events ?? []).filter((e) => e.kind === "run");
  // №22.4.1 (F1) — "working" is now a LIVE claim, bounded server-side: an open
  // `agent_runs` row only counts while it could still be executing, and the verb
  // comes from that run's own evidence. So this flag can be trusted to mean
  // "executing right now" — which it could not before (an abandoned run row kept
  // an agent at "working" indefinitely, replaying an old activity subject as "now:").
  const isWorking = st?.status === "working";
  const pendingAnswerFor = answer.isPending ? answer.variables?.taskId : null;
  // №22.2 (D-CV1) — resolve answered cards in place (a receipt), don't vanish them.
  // №22.3 (D-CH2) — and watch the run in the panel (the page doesn't move).
  const chooseAndRecord = (t: Task, optionIndex: number) => {
    const opt = t.needs_you_options?.[optionIndex];
    coPanel.notifyAnswered({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name, label: anchorLabelOf(t) });
    answer.mutate(
      { taskId: t.id, optionIndex, ...(opt?.label ? { optionLabel: opt.label } : {}) },
      {
        onSuccess: () =>
          ja.record(t.id, {
            optionIndex, label: opt?.label ?? "your choice", at: new Date().toISOString(), outcome: "executed",
            reason: t.needs_you_reason ?? "", options: t.needs_you_options ?? [], evidence: t.needs_you_evidence ?? [],
            agentName: t.agent_name, anchorLabel: anchorLabelOf(t),
            // №22.5 (F1) — the receipt belongs to THIS question, not to the task.
            ...(t.needs_you_batch ? { batchIndex: t.needs_you_batch.index } : {}),
          }),
      },
    );
  };

  /**
   * №22.5 (F10) — answering a card IN YOUR OWN WORDS, from the desk.
   *
   * The same path typing in the thread takes: it runs the hire with those words as the
   * input, which since F6 arrives alongside the question they answer. The panel focuses
   * first, so the run is watched rather than fired into the dark (F2).
   */
  const replyToCard = (t: Task, text: string) => {
    openThread(t);
    play.mutate({ agentId: t.agent_id ?? agentId!, taskId: t.id, input: text, requestedByName: "you" });
  };

  /**
   * №22.5 (C4/F1) — skipping records the same kind of receipt as answering, so the row
   * shows what happened instead of quietly re-rendering. On a batch it advances, and
   * `receiptFor` then lets the next question through immediately.
   */
  const skipAndRecord = (t: Task) => {
    coPanel.notifyAnswered({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name, label: anchorLabelOf(t) });
    skip.mutate({ taskId: t.id }, {
      onSuccess: (res) => ja.record(t.id, {
        optionIndex: -1, label: "Skipped", at: new Date().toISOString(), outcome: "skipped",
        reason: t.needs_you_reason ?? "", options: t.needs_you_options ?? [], evidence: t.needs_you_evidence ?? [],
        agentName: t.agent_name, anchorLabel: anchorLabelOf(t),
        ...(t.needs_you_batch ? { batchIndex: t.needs_you_batch.index } : {}),
        ...(res?.consequence ? { consequence: res.consequence } : {}),
      }),
    });
  };

  // №22.1 (D-CC7) — DOES is the page. One task list, split cleanly:
  //  • Needs-You → decision cards (the SAME component as the cockpit, D-CC4);
  //  • Working now → ONLY when the agent is actually running (presence=working); an
  //    inbox/untriaged task is never "working now" — it just queues;
  //  • Queued → everything else that isn't a live card.
  // `current` (rank #1) is ALSO present in `up_next` (the full queue) — dedupe by id so a
  // task never renders twice.
  const allTasks: Task[] = [];
  const seenTaskIds = new Set<string>();
  for (const t of [current, ...upNext]) {
    if (t && !seenTaskIds.has(t.id)) { seenTaskIds.add(t.id); allTasks.push(t); }
  }
  const needsYouTasks = allTasks.filter((t) => !!t.needs_you_reason?.trim());
  // №22.2 (D-CV1) — receipts whose task already left the query (the reason cleared on
  // answer); render them until the desk's next visit so the answer leaves a trace here.
  const nyIds = new Set(needsYouTasks.map((t) => t.id));
  const answeredExtraDesk = Object.entries(ja.map).filter(([id]) => !nyIds.has(id));
  // №22.4.1 (F1) — the working card is the task the LIVE RUN declared, not
  // whatever happens to be rank #1. The run knows its own task; falling back to
  // `current` only when it didn't record one keeps older runs rendering.
  const liveTask = st?.working_task_id
    ? (allTasks.find((t) => t.id === st.working_task_id) ?? null)
    : null;
  const workingTask = isWorking
    ? (liveTask ?? (current && !current.needs_you_reason?.trim() ? current : null))
    : null;
  const queued = allTasks.filter((t) => !t.needs_you_reason?.trim() && t.id !== workingTask?.id);
  // A materialised workflow step IS already a task (shown above) — don't also render it
  // as a separate step row.
  //
  // №22.6 — this asked the question by STRING: `t.title === w.name && t.anchor?.no ===
  // w.subject_no`. Two failure modes, both silent and both David's: a task whose title
  // no longer matches its step (renamed, or the step renamed) and a task past the
  // queue's cut-off both made the SAME work render twice — once as a task card with ▶,
  // once as a step row without one, in the same list, looking unaccountably different.
  // №16 S3 put `task_id` on the step row for exactly this; nobody served it until now.
  // Identity, not resemblance.
  const wfSteps = (snap.data?.workflow_steps ?? []).filter(
    (w) => !w.task_id || !seenTaskIds.has(w.task_id),
  );

  // №22.2 (D-CV4/D-CV6) — why a queued task isn't running yet is the DIAL, stated in
  // words (not a bare glyph): supervised/off wait for ▶; autonomous runs on the cycle.
  const autonomy = cfg?.autonomy ?? "off";
  const whyRun = autonomy === "autonomous"
    ? "runs automatically"
    : autonomy === "off"
      ? "held — drafts only until you press ▶"
      : "runs when you press ▶";
  // №22.4.1 (F2) — a work card opens the THREAD IN THE PANEL. The page does not
  // move. This is the D-CH4 ruling applied to the rows that were exempted from it
  // in 22.3 ("desk work-rows keep their Link to the cockpit"); that exemption is
  // what let the regression back in, and it is withdrawn — see
  // `thread-affordances.ts`, which enumerates every affordance and gates this.
  const openThread = (t: Task) =>
    coPanel.focusTask({ taskId: t.id, agentId: t.agent_id ?? agentId, agentName: t.agent_name ?? n.name, label: anchorLabelOf(t) });

  // №22.4.1 (F4) — one card component for every work row. Whole card = the click
  // target; the run control is the only child that isn't.
  const workCard = (t: Task, opts: { live?: boolean } = {}) => {
    const meta = [anchorLabelOf(t), t.due_by ? `due ${t.due_by}` : null, opts.live ? null : t.status]
      .filter(Boolean).join(" · ");
    const running = play.isPending && play.variables?.taskId === t.id;
    return (
      <div
        className={`workcard${opts.live ? " workcard--live" : ""}`}
        key={t.id}
        role="button"
        tabIndex={0}
        data-testid="work-card"
        data-task-id={t.id}
        onClick={() => openThread(t)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openThread(t); } }}
      >
        {/* The locked mockup's leading state glyph (`.wk .ic`): a faint ring for
            queued work, the accent ▶ for the row that is actually running. It is
            the fastest read on the page — which row is live — and it is paired
            with a state WORD below (D-CV4), never carrying the meaning alone. */}
        <span className={`workcard__ic${opts.live ? " workcard__ic--run" : ""}`} aria-hidden="true">
          {opts.live ? "▶" : "○"}
        </span>
        <div className="workcard__body">
          <div className="workcard__title">{t.title}</div>
          <div className="workcard__meta">{meta || (opts.live ? "running" : "queued")}</div>
          {opts.live
            // F1 — the live line, and SINCE WHEN. The timestamp is the CONDITION,
            // not a decoration: a "now:" with nothing to check it against is the
            // defect this lane exists to kill, so the render refuses to produce
            // one. Enforced here as well as in the engine (the 11.2 pattern) — if
            // a future change let a stale run claim "working" again, the worst it
            // could do is show an undated card, not lie about the present.
            ? st?.since && (
              <div className="workcard__now" data-testid="live-now">
                {presenceVerb ? `now: ${presenceVerb}…` : "now: running…"}
                <span className="workcard__since"> · since {formatClockTime(st.since)}</span>
              </div>
            )
            // F3 — what created it, then why it isn't running yet (D-CV4/D-CV6).
            : <div className="workcard__prov"><ProvenanceLine p={t.provenance} /> · {whyRun}</div>}
        </div>
        {opts.live
          ? <span className="workcard__go">watch thread →</span>
          : (
            /* D-CV6 — the affordance on agent work is "Run to complete" (the agent
               works the brief + ticks the step in-thread), not a bare ▶ nor a human
               tick. F4: a real button, and it runs — it does not just navigate. */
            <button
              type="button"
              className="workcard__run"
              disabled={play.isPending}
              data-testid="run-to-complete"
              onClick={(e) => {
                e.stopPropagation();
                openThread(t);
                // №22.4.2 (B) — no input. The RUNNER composes the task's brief,
                // anchor and title into the run; passing `t.instructions` here (as
                // this line did) made a raw field the entire ask, which is how a
                // task whose brief had been overwritten reached the model as a
                // context-free status note.
                play.mutate({ agentId: t.agent_id ?? agentId!, taskId: t.id, input: "", requestedByName: "the desk" });
              }}
            >
              {running ? "Running…" : "▶ Run to complete"}
            </button>
          )}
      </div>
    );
  };

  return (
    <div className="screen">
      {/* №29 (S2) — the DESK subject's crumb. `presence` is a real read
          (`get_workforce_status`, №22.4.1 F1: "live means executing now"), so the
          chip is a fact about the hire rather than a drawn state — and it is the
          probe's own "● running · {verb}" when the hire actually is. */}
      <DeskStageHead working={isWorking} presence={presence} verb={presenceVerb} />
      {/* ═══ IS — the identity plane (D-CC7) ═══
          №29 (S2): the hire's NAME and its LIVE STATE moved to the stage head — the
          head IS this surface's identity band now, and it was already rendering the
          name as the anchor (the desk row's anchor is the hire node). What is left
          here is what the head cannot carry: the mark, the wall, the roles it holds,
          how many areas it acts in, and the autonomy dial. */}
      <div style={strip}>
        <span className="avatar av-md" style={{ background: ramp }}>{initial}</span>
        {template && <span style={chip}>from a template</span>}
        <div style={{ display: "flex", gap: 7, marginLeft: 8, flexWrap: "wrap", alignItems: "center" }}>
          {caps.wallHead && <span style={wallChip}>✕ cannot {caps.wallHead}</span>}
          {caps.roles.length
            ? caps.roles.map((r) => <span key={r} style={chip}>{r}</span>)
            : <span style={chip}>Legal verbs · no role</span>}
          <span style={chip}>{caps.can.length} area{caps.can.length === 1 ? "" : "s"}</span>
          {/* №31 (D-CE1) — the ADDRESS is the hire's name. It sits on the identity
              plane beside the roles and the wall because that is what it is: another
              way to reach this member of staff, not a setting. Rendered only when the
              whole address resolved (see above). */}
          {emailAddress && <HireAddress address={emailAddress} />}
        </div>
        <div style={{ marginLeft: "auto" }}>
          <AutonomyDial compact agentId={agentId!} cfg={cfg} wallReason={caps.wallReason} onChanged={() => node.refetch()} />
        </div>
      </div>

      {/* ═══ DOES = the page · KNOW / access / activity = the rail ═══ */}
      <div style={grid}>
        <div>
          {/* Needs you — the SAME decision card, compact (D-CC4). №22.2: answered
              cards resolve in place to a receipt (D-CV1), they don't vanish. */}
          {(needsYouTasks.length > 0 || answeredExtraDesk.length > 0) && (
            <section style={sect}>
              {/* №22.5 (F1) — the count follows the SAME rule the rows do: a receipt
                  hides a row only while it still describes that row's question. It read
                  0 over a live, unanswerable card before this. */}
              <div className="sechead"><h2>Needs you</h2><span className="n">{needsYouTasks.filter((t) => !receiptFor(ja.map[t.id], t)).length}</span><div className="rule" /></div>
              <div className="stack">
                {needsYouTasks.map((t) => {
                  const r = receiptFor(ja.map[t.id], t);
                  return (
                    <DecisionCard
                      key={t.id}
                      variant={r ? "full" : "compact"}
                      reason={t.needs_you_reason ?? ""}
                      evidence={t.needs_you_evidence}
                      options={t.needs_you_options}
                      context={{ anchorLabel: anchorLabelOf(t) }}
                      resolved={r ? { optionIndex: r.optionIndex, label: r.label, at: r.at, outcome: r.outcome } : null}
                      onChoose={r ? undefined : (optionIndex) => chooseAndRecord(t, optionIndex)}
                      choosing={pendingAnswerFor === t.id}
                      chosenIndex={pendingAnswerFor === t.id ? (answer.variables?.optionIndex ?? null) : null}
                      // №22.5 (C1/C4) — same card, same data, fourth surface (D-CC4).
                      batch={t.needs_you_batch ?? null}
                      provenance={t.provenance ?? null}
                      skipConsequence={t.needs_you_skip_consequence ?? null}
                      skipped={t.needs_you_skipped}
                      onSkip={r ? undefined : () => skipAndRecord(t)}
                      onReply={r ? undefined : (text) => replyToCard(t, text)}
                      replying={play.isPending && play.variables?.taskId === t.id}
                      skipping={skip.isPending && skip.variables?.taskId === t.id}
                      openThreadHref={`/app/task/${t.id}`}
                      onOpenThread={() => coPanel.focusTask({ taskId: t.id, agentId: t.agent_id, agentName: t.agent_name, label: anchorLabelOf(t) })}
                      error={answer.isError && answer.variables?.taskId === t.id ? ((answer.error as Error)?.message ?? "That option couldn’t be completed.") : null}
                    />
                  );
                })}
                {answeredExtraDesk.map(([id, r]: [string, AnsweredReceipt]) => (
                  <DecisionCard
                    key={id}
                    variant="full"
                    reason={r.reason}
                    options={r.options}
                    context={{ anchorLabel: r.anchorLabel }}
                    resolved={{ optionIndex: r.optionIndex, label: r.label, at: r.at, outcome: r.outcome }}
                    openThreadHref={`/app/task/${id}`}
                    onOpenThread={() => coPanel.focusTask({ taskId: id, agentName: r.agentName, label: r.anchorLabel })}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Awaiting your signature — the agent's prepared MONEY (I9). The SAME interactive
              Review-&-sign row as the Needs-You home; never one-click, content-hashed (D-CC8). */}
          {raisedAuths.length > 0 && (
            <section style={sect}>
              <div className="sechead"><h2>Awaiting your signature</h2><span className="n">money — Review &amp; sign, never one-click</span><div className="rule" /></div>
              <div className="inbox">
                {raisedAuths.map((a) => <AuthRow key={a.id} auth={a} />)}
              </div>
            </section>
          )}

          {/* Working now — №22.4.1 (F1). The section ALWAYS renders: when nothing is
              executing it says so and points at what's waiting. Before, the section
              simply vanished, which meant an agent with a stale open run row showed a
              card claiming "now: …" and an idle agent showed nothing at all — a
              supervisor could not tell "nothing running" from "not rendered". */}
          <section style={sect}>
            <div className="sechead"><h2>Working now</h2><div className="rule" /></div>
            {workingTask
              ? workCard(workingTask, { live: true })
              : (
                <div className="worknone" data-testid="working-now-empty">
                  nothing running{queued.length > 0 ? ` — ${queued.length} queued` : " — nothing queued"}
                  {/* Say what is actually true: the run row is still open, we have
                      simply stopped counting it as live. Claiming it was "closed
                      out" would be inventing an action nobody took. */}
                  {st?.stale_run && (
                    <> · an earlier run never recorded an ending — it is no longer counted as running</>
                  )}
                </div>
              )}
          </section>

          {/* Queued — tasks + workflow steps, in the same card family (F4). */}
          {(queued.length > 0 || wfSteps.length > 0) && (
            <section style={sect}>
              <div className="sechead">
                <h2>Queued</h2>
                <span className="n">{queued.length + wfSteps.length}</span>
                {/* F3 — the mockup's section explainer: what this list IS. */}
                <span className="n">waiting to run — ▶ or autonomous</span>
                <div className="rule" />
              </div>
              {queued.map((t) => workCard(t))}
              {/* №22.6 — a step with no materialised task is STILL WORK YOU CAN FINISH.
                  It rendered as a link to the process with no verb, beside task cards
                  carrying ▶ — and wearing "held — drafts only until you press ▶", a
                  condition stated for a control it did not have. David: "Why are there
                  buttons to Complete not available? … when I click into them via Open
                  the process, they are actionable with a Complete button."

                  They are. `workflow_advance` is that button, and the desk already had
                  everything it needs. The card now carries the verb, the resolved
                  matter name, and a provenance line that is true of a step. The route
                  to the process stays as the secondary affordance — it is where the
                  rest of the process lives, which a single step cannot show. */}
              {wfSteps.map((s) => (
                <div className="workcard" key={`wf-${s.step_entry_no}`} data-testid="wf-step-card">
                  <span className="workcard__ic" aria-hidden="true">○</span>
                  <div className="workcard__body">
                    <div className="workcard__title">{s.name}</div>
                    <div className="workcard__meta">
                      {s.subject_label}{s.due_date ? ` · due ${s.due_date}` : ""} · {s.status}
                    </div>
                    {/* NOT `whyRun`: that line explains why a TASK is waiting for ▶, and
                        this is not a task and has no ▶. What is true of a step is that
                        nobody has ticked it yet, and that you can. */}
                    <div className="workcard__prov">
                      from <strong>a workflow step</strong> · nothing to run — it is yours to tick
                    </div>
                  </div>
                  <Link
                    className="workcard__go"
                    to={`/app/place/${s.subject_type || "matter"}/${s.subject_no}/process?lens=drive#seg-${s.step_entry_no}`}
                  >
                    open the process →
                  </Link>
                  <button
                    type="button"
                    className="runbtn"
                    data-testid="wf-step-complete"
                    disabled={advance.isPending}
                    onClick={() => advance.mutate({ instance_no: s.instance_no, step_entry_no: s.step_entry_no })}
                  >
                    {advance.isPending && advance.variables?.step_entry_no === s.step_entry_no ? "Completing…" : "✓ Complete"}
                  </button>
                </div>
              ))}
            </section>
          )}

          {/* Routines — declared recurring duties. Kuvio records; a host (or ▶ Run now)
              executes. The cadence is opaque text (cron / trigger phrase / "ad-hoc"). */}
          {rtns.length > 0 && (
            <section style={sect}>
              <div className="sechead"><h2>Routines</h2><span className="n">declared duties · run on a cadence</span><div className="rule" /></div>
              {rtns.map((r) => {
                const running = runRoutine.isPending && runRoutine.variables === r.id;
                return (
                  <div className="workrow" key={r.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="meta" style={{ flex: 1 }}>
                      <div className="nm">↻ {r.name}{!r.enabled ? " · paused" : ""}</div>
                      <div className="st">
                        {r.cadence}
                        {r.last_run_at ? ` · last run ${formatRelativeTime(r.last_run_at)}${r.last_run_outcome ? ` (${r.last_run_outcome})` : ""}` : " · not run yet"}
                      </div>
                    </div>
                    {/* №22.5 (F2) — a routine fires real work, so it opens a surface to
                        watch it on, same as ▶ does. A routine is not bound to one task
                        thread, so the honest target is this desk's own STREAM — where
                        the runs it produces land. */}
                    <button
                      type="button"
                      style={runBtn}
                      disabled={runRoutine.isPending}
                      onClick={() => { coPanel.setExpanded(true); coPanel.showStream(); runRoutine.mutate(r.id); }}
                    >
                      {running ? "Running…" : "Run now"}
                    </button>
                  </div>
                );
              })}
            </section>
          )}

          {/* What ran — WHILE YOU WERE AWAY, from real run data (D-CV8 + №22.4 item D).
              Every row is a RUN and says how it ended: done-with-receipts, needs-you,
              blocked, or — the one that matters — nothing at all. */}
          <section style={sect}>
            <div className="sechead">
              <h2>What ran</h2>
              <span className="n">{deskRuns.length} recent · while you were away</span>
              <div className="rule" />
            </div>
            {deskRuns.length === 0 ? (
              <div className="muted tiny" style={{ padding: "4px 2px" }}>Nothing yet — what {n.name} does on its own will land here.</div>
            ) : (
              <div data-testid="wywa-runs">
                {deskRuns.slice(0, 6).map((e) => {
                  const end = runEnding(e);
                  return (
                    <div
                      className="workrow" key={e.id}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: e.taskId ? "pointer" : "default" }}
                      onClick={() => { if (e.taskId) coPanel.focusTask({ taskId: e.taskId }); }}
                    >
                      <span style={{ color: end.colour, fontWeight: 700, flex: "0 0 auto" }}>{end.glyph}</span>
                      <div className="meta" style={{ flex: 1, minWidth: 0 }}>
                        <div className="nm">{e.title}</div>
                        <div className="st">
                          {[end.label, e.actionCount ? `${e.actionCount} actions` : null, formatRelativeTime(e.ts)]
                            .filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {(e.refusals ?? 0) > 0 && (
                        <span className="muted tiny" style={{ flex: "0 0 auto" }} title="the receipts gate refused a bare tick">
                          {e.refusals} refused
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* ═══ right rail — KNOW · access · activity ═══ */}
        <div>
          <div className="card" style={railCard}>
            <h3 style={railH}>Persona</h3>
            <div style={railBody}>
              {persona ? persona : <span className="muted tiny">No persona set — write one in the console. Until then the module default brief applies.</span>}
              {template && <div style={{ marginTop: 8 }}><span className="provchip">Hired from template · {template.template_id} v{template.template_version}</span></div>}
            </div>
          </div>

          {/* №29 (S2) — "Knows" and "Activity" were DELETED here, not moved: the
              shell's work rail already renders this hire's learnings (Memory) and
              its run history (Receipts) from the same reads, bound to this desk
              because the route's anchor IS the hire. Two of the five cards in this
              column were a third work rail inside the stage — after the run
              subject's (§S2) and beside the real one. What is left is the desk's
              OWN configuration, which the rail deliberately does not carry. */}
          <div className="card" style={railCard}>
            <h3 style={railH}>Access &amp; venues</h3>
            <div style={railBody}>
              <div style={venueLn}><span>In-app + any connected host</span><span style={{ color: "var(--ok)", fontSize: 11 }}>served live · nothing to install</span></div>
              <details>
                <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--accent)", fontWeight: 600, padding: "8px 0 4px", listStyle: "none" }}>Headless kit &amp; tokens →</summary>
                <div style={{ marginTop: 8 }}><ExportKitSection agentId={agentId!} agentName={n.name} /></div>
              </details>
            </div>
          </div>

          {/* Can & cannot — the 11.2 capability detail (roles · areas · the wall),
              derived live from role/area data. Restores the Equipment-tab detail. */}
          <div className="card" style={railCard}>
            <h3 style={railH}>Can &amp; cannot · 11.2</h3>
            <div style={railBody}>
              <div className="plane-eyebrow" style={{ marginBottom: 6 }}>Roles held</div>
              <div style={{ marginBottom: 10 }}>
                {caps.roles.length ? caps.roles.map((r) => <span key={r} className="statuschip" style={{ marginRight: 5 }}>{r}</span>) : <span className="muted tiny">No role — default member verbs.</span>}
              </div>
              <div className="plane-eyebrow" style={{ marginBottom: 6 }}>Acts in · {caps.can.length} area{caps.can.length === 1 ? "" : "s"}</div>
              <div className="verblist" style={{ marginBottom: 10 }}>
                {caps.can.length ? caps.can.map((a) => <span key={a.key} className={`verbchip${a.money ? " verbchip--money" : ""}`}>{a.key}</span>) : <span className="muted tiny">—</span>}
              </div>
              <div className="plane-eyebrow plane-eyebrow--wall" style={{ marginBottom: 6 }}>The wall · cannot</div>
              <div className="verblist">
                {caps.wall.length ? caps.wall.map((a) => <span key={a.key} className="verbchip blocked">{a.key}</span>) : <span className="muted tiny">—</span>}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── one-page desk styles (token-only) ────────────────────────────────────────
/**
 * №31 (D-CE1) — the hire's email address with a copy affordance.
 *
 * A chip, not a field: it is identity, and it sits with the roles rather than in a
 * settings pane. The address is monospace because it is a string a person retypes,
 * and the whole chip is the copy button because "select the text without the label"
 * is the fiddliest possible interaction on the one element people will actually use.
 *
 * The confirmation is a local state flip, not a toast — a toast for a clipboard write
 * is noise, and the label returning to the address after a beat says the same thing.
 */
function HireAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      style={addrChip}
      title={`Copy ${address}`}
      aria-label={`Copy ${address} to the clipboard`}
      onClick={() => {
        // A clipboard write can be refused (permissions, insecure context). Failing
        // silently would leave the user believing they copied it — so the label says
        // so, and the address stays on screen to be selected by hand.
        navigator.clipboard?.writeText(address).then(
          () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
          () => { setCopied(false); },
        );
      }}
    >
      <i className="ti ti-mail" aria-hidden="true" style={{ fontSize: 12 }} />
      <span style={{ fontFamily: "var(--font-mono)" }}>{address}</span>
      <i className={copied ? "ti ti-check" : "ti ti-copy"} aria-hidden="true" style={{ fontSize: 12, opacity: 0.75 }} />
    </button>
  );
}

const strip: CSSProperties = { display: "flex", alignItems: "center", gap: 14, background: "var(--surface-1)", border: "1px solid var(--border-strong)", borderRadius: 14, padding: "14px 18px", marginBottom: 16 };
const addrChip: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 11.5, padding: "3px 10px", borderRadius: 9, whiteSpace: "nowrap", cursor: "pointer" };
const chip: CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 11.5, padding: "3px 10px", borderRadius: 9, whiteSpace: "nowrap" };
const wallChip: CSSProperties = { background: "color-mix(in srgb, var(--danger) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--danger) 32%, transparent)", color: "var(--danger)", fontSize: 11.5, fontWeight: 600, padding: "3px 10px", borderRadius: 9, whiteSpace: "nowrap" };
const grid: CSSProperties = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 316px", gap: 16, alignItems: "start" };
const sect: CSSProperties = { marginBottom: 14 };
const railCard: CSSProperties = { marginBottom: 14, padding: "12px 15px" };
const railH: CSSProperties = { fontSize: 11, letterSpacing: "0.06em", color: "var(--text-3)", fontWeight: 600, textTransform: "uppercase", marginBottom: 8 };
const railBody: CSSProperties = { fontSize: 12.5, color: "var(--text-2)", lineHeight: 1.5 };
const venueLn: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 6, borderBottom: "1px solid var(--border)" };
const runBtn: CSSProperties = { flex: "0 0 auto", border: "1.5px solid var(--accent)", background: "var(--surface-1)", color: "var(--accent)", borderRadius: 9, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" };

/**
 * №29 (S2) — the desk's stage-head declaration, as its own component so the page's
 * hook order is untouched by it and so the crumb re-declares only when the hire's
 * live state actually changes.
 */
function DeskStageHead({ working, presence, verb }: { working: boolean; presence: string; verb: string | null }) {
  useStageHead({
    state: working
      ? { tone: "running", label: verb ? `running · ${verb}` : "running" }
      : { tone: "neutral", label: presence },
  });
  return null;
}
